/* ==========================================================================
   SKNBODY HOMEPAGE - BLOCK 04
   File: assets/skn-home-the-idea.js

   Responsibilities:
   - Reveal section elements as they enter the viewport
   - Preserve visible content when JS is unavailable
   - Respect reduced-motion preferences
   - Support Shopify Theme Editor section reloads
   ========================================================================== */

(function () {
  'use strict';

  const ROOT_SELECTOR =
    '[data-skn-home-idea]';

  if (window.SKNHomeIdea) {
    window.SKNHomeIdea.initAll(document);
    return;
  }

  const sectionStates = new WeakMap();


  /* ==========================================================================
     HELPERS
     ========================================================================== */

  function getReducedMotionQuery() {
    return window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );
  }


  /* ==========================================================================
     INITIALIZE SECTION
     ========================================================================== */

  function initSection(root) {
    if (!root || sectionStates.has(root)) {
      return;
    }

    const revealElements = Array.from(
      root.querySelectorAll(
        '[data-idea-reveal]'
      )
    );

    const reducedMotionQuery =
      getReducedMotionQuery();

    const imageSwitchers = Array.from(
      root.querySelectorAll('[data-idea-switch]')
    );

    const switcherCleanups = [];

    let observer = null;
    let destroyed = false;

    root.classList.add('has-js');


    /* ------------------------------------------------------------------------
       PRODUCT STORY IMAGE SWITCH
       Mouse: preview on hover; restore on leave.
       Touch / keyboard: toggle without requiring hover.
       An image-load failure never replaces the visible product with a blank.
       ------------------------------------------------------------------------ */

    const alternateLoads = new WeakMap();
    const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

    function hydrateAlternateImage(button) {
      const image = button.querySelector('[data-idea-alt-image]');
      if (!image) return Promise.resolve(false);
      if (image.complete && image.naturalWidth > 0) return Promise.resolve(true);
      if (alternateLoads.has(image)) return alternateLoads.get(image);

      const promise = new Promise(function (resolve) {
        let finished = false;
        let timer;
        function finish(ok) {
          if (finished) return;
          finished = true;
          window.clearTimeout(timer);
          image.removeEventListener('load', onLoad);
          image.removeEventListener('error', onError);
          resolve(ok);
        }
        function onLoad() { finish(image.naturalWidth > 0); }
        function onError() { finish(false); }
        image.addEventListener('load', onLoad);
        image.addEventListener('error', onError);
        timer = window.setTimeout(function () { finish(false); }, 12000);
        // Interaction is explicit intent. A hidden loading="lazy" image can
        // otherwise remain deferred even while decode() is awaited.
        image.loading = 'eager';
        if (image.dataset.srcset) {
          image.srcset = image.dataset.srcset;
          image.removeAttribute('data-srcset');
        }
        if (image.dataset.src) {
          image.src = image.dataset.src;
          image.removeAttribute('data-src');
        }
        if (image.complete && image.naturalWidth > 0) onLoad();
      });
      alternateLoads.set(image, promise);
      promise.then(function (ok) {
        if (!ok) alternateLoads.delete(image);
      });
      return promise;
    }

    function setSwitcherState(button, swapped) {
      button.classList.toggle('is-swapped', swapped);
      button.setAttribute('aria-pressed', swapped ? 'true' : 'false');
      const index = button.querySelector('[data-idea-index]');
      if (index) index.textContent = swapped ? '02 / 02' : '01 / 02';
    }

    imageSwitchers.forEach(function (button) {
      let wantedAlternate = false;
      let mouseInside = false;
      let keyboardSelected = false;
      let generation = 0;
      let disposed = false;
      let lastPointerType = '';

      function requestState(swapped) {
        wantedAlternate = swapped;
        const token = ++generation;
        if (!swapped) {
          setSwitcherState(button, false);
          return;
        }
        hydrateAlternateImage(button).then(function (loaded) {
          if (disposed || destroyed || token !== generation || !wantedAlternate) return;
          if (loaded) setSwitcherState(button, true);
          else wantedAlternate = false;
        });
      }

      function onEnter(event) {
        if (!hoverQuery.matches || event.pointerType === 'touch') return;
        mouseInside = true;
        requestState(true);
      }
      function onLeave(event) {
        if (event.pointerType === 'touch') return;
        mouseInside = false;
        keyboardSelected = false;
        // Incrementing the generation also cancels a pending hover request.
        requestState(false);
      }
      function onPointerDown(event) { lastPointerType = event.pointerType; }
      function onFocus() { hydrateAlternateImage(button); }
      function onBlur() {
        if (keyboardSelected && !mouseInside) {
          keyboardSelected = false;
          requestState(false);
        }
      }
      function onClick(event) {
        const isKeyboard = event.detail === 0;
        if (!isKeyboard && hoverQuery.matches && mouseInside && lastPointerType !== 'touch') {
          // A mouse click must not undo the photo that hover is displaying.
          requestState(true);
          return;
        }
        keyboardSelected = isKeyboard;
        requestState(!wantedAlternate);
      }
      function onHoverCapabilityChange() {
        mouseInside = false;
        keyboardSelected = false;
        requestState(false);
      }

      button.addEventListener('pointerenter', onEnter, { passive: true });
      button.addEventListener('pointerleave', onLeave, { passive: true });
      button.addEventListener('pointerdown', onPointerDown, { passive: true });
      button.addEventListener('focus', onFocus);
      button.addEventListener('blur', onBlur);
      button.addEventListener('click', onClick);
      if (hoverQuery.addEventListener) hoverQuery.addEventListener('change', onHoverCapabilityChange);
      else if (hoverQuery.addListener) hoverQuery.addListener(onHoverCapabilityChange);

      switcherCleanups.push(function () {
        disposed = true;
        generation += 1;
        button.removeEventListener('pointerenter', onEnter);
        button.removeEventListener('pointerleave', onLeave);
        button.removeEventListener('pointerdown', onPointerDown);
        button.removeEventListener('focus', onFocus);
        button.removeEventListener('blur', onBlur);
        button.removeEventListener('click', onClick);
        if (hoverQuery.removeEventListener) hoverQuery.removeEventListener('change', onHoverCapabilityChange);
        else if (hoverQuery.removeListener) hoverQuery.removeListener(onHoverCapabilityChange);
        setSwitcherState(button, false);
      });
    });


    /* ------------------------------------------------------------------------
       REVEAL FUNCTIONS
       ------------------------------------------------------------------------ */

    function revealElement(element) {
      if (!element) return;

      element.classList.add('is-visible');
    }

    function revealAll() {
      revealElements.forEach(revealElement);
    }

    function resetRevealState() {
      revealElements.forEach(function (element) {
        element.classList.remove('is-visible');
      });
    }


    /* ------------------------------------------------------------------------
       OBSERVER
       ------------------------------------------------------------------------ */

    function createObserver() {
      if (observer) {
        observer.disconnect();
        observer = null;
      }

      if (
        destroyed ||
        reducedMotionQuery.matches ||
        !('IntersectionObserver' in window)
      ) {
        revealAll();
        return;
      }

      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) {
              return;
            }

            revealElement(entry.target);
            observer.unobserve(entry.target);
          });
        },
        {
          rootMargin:
            '0px 0px -8% 0px',

          threshold: 0.12
        }
      );

      revealElements.forEach(function (element) {
        observer.observe(element);
      });
    }


    /* ------------------------------------------------------------------------
       MOTION PREFERENCE
       ------------------------------------------------------------------------ */

    function handleMotionPreferenceChange() {
      if (reducedMotionQuery.matches) {
        if (observer) {
          observer.disconnect();
          observer = null;
        }

        revealAll();
      } else {
        resetRevealState();
        createObserver();
      }
    }

    if (
      typeof reducedMotionQuery.addEventListener ===
      'function'
    ) {
      reducedMotionQuery.addEventListener(
        'change',
        handleMotionPreferenceChange
      );
    } else if (
      typeof reducedMotionQuery.addListener ===
      'function'
    ) {
      reducedMotionQuery.addListener(
        handleMotionPreferenceChange
      );
    }


    /* ------------------------------------------------------------------------
       INITIAL STATE
       ------------------------------------------------------------------------ */

    /*
      In the Shopify editor, keep everything visible so editing
      settings never leaves cards temporarily hidden.
    */

    if (
      window.Shopify &&
      window.Shopify.designMode
    ) {
      revealAll();
    } else {
      createObserver();
    }


    /* ------------------------------------------------------------------------
       CLEANUP
       ------------------------------------------------------------------------ */

    function destroy() {
      destroyed = true;

      switcherCleanups.forEach(function (cleanup) {
        cleanup();
      });

      if (observer) {
        observer.disconnect();
      }

      if (
        typeof reducedMotionQuery.removeEventListener ===
        'function'
      ) {
        reducedMotionQuery.removeEventListener(
          'change',
          handleMotionPreferenceChange
        );
      } else if (
        typeof reducedMotionQuery.removeListener ===
        'function'
      ) {
        reducedMotionQuery.removeListener(
          handleMotionPreferenceChange
        );
      }

      root.classList.remove('has-js');

      revealAll();

      sectionStates.delete(root);
    }

    sectionStates.set(root, {
      destroy: destroy,
      revealAll: revealAll
    });
  }


  /* ==========================================================================
     INITIALIZE ALL
     ========================================================================== */

  function initAll(context) {
    const scope = context || document;

    if (
      scope.matches &&
      scope.matches(ROOT_SELECTOR)
    ) {
      initSection(scope);
    }

    scope
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(initSection);
  }

  function destroyAll(context) {
    const scope = context || document;
    const roots = [];

    if (
      scope.matches &&
      scope.matches(ROOT_SELECTOR)
    ) {
      roots.push(scope);
    }

    scope
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(function (root) {
        roots.push(root);
      });

    roots.forEach(function (root) {
      const state =
        sectionStates.get(root);

      if (
        state &&
        typeof state.destroy === 'function'
      ) {
        state.destroy();
      }
    });
  }


  /* ==========================================================================
     PUBLIC API
     ========================================================================== */

  window.SKNHomeIdea = {
    initAll: initAll,
    destroyAll: destroyAll
  };


  /* ==========================================================================
     INITIAL PAGE LOAD
     ========================================================================== */

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        initAll(document);
      },
      {
        once: true
      }
    );
  } else {
    initAll(document);
  }


  /* ==========================================================================
     SHOPIFY THEME EDITOR
     ========================================================================== */

  document.addEventListener(
    'shopify:section:load',
    function (event) {
      initAll(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    function (event) {
      destroyAll(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:select',
    function (event) {
      const root = event.target.matches &&
        event.target.matches(ROOT_SELECTOR)
          ? event.target
          : event.target.querySelector
            ? event.target.querySelector(
                ROOT_SELECTOR
              )
            : null;

      if (!root) return;

      const state =
        sectionStates.get(root);

      if (
        state &&
        typeof state.revealAll === 'function'
      ) {
        state.revealAll();
      }
    }
  );
})();